import fallImage from '$src/assets/images/fall.png'
import springImage from '$src/assets/images/spring.png'
import summerImage from '$src/assets/images/summer.png'
import winterImage from '$src/assets/images/winter.png'
import { AttachmentResponse, MaintenanceTaskWithSteps } from '@markku/types'
import { partition, sortBy } from '@markku/utils'
import { msg, type MacroMessageDescriptor } from '@lingui/core/macro'
import dayjs from 'dayjs'

// Type definitions
export interface MaintenanceTask {
  id: string
  title: string
  completed: boolean
  isUserTask: boolean
  isSystemTask: boolean
  createdByUserId: number | null
  category?: string
  description?: string
  deadline?: string
  attachments?: AttachmentResponse[]
  instructions?: string[]
  lastCompleted?: string
  nextScheduled?: string
  estimatedDurationHours?: number
}

export interface SeasonData {
  season: string
  seasonName: MacroMessageDescriptor
  year: number
  tasks: MaintenanceTask[]
  recommendedTasks: MaintenanceTask[]
  markkuTasks: MaintenanceTask[]
  completedCount: number
  totalCount: number
  monthRange: MacroMessageDescriptor
  backgroundImage: string
  backgroundColor: string
  totalCost: number
}

export const SEASON_ORDER = ['spring', 'summer', 'fall', 'winter'] as const
export type Season = (typeof SEASON_ORDER)[number]

export interface TargetSeason {
  season: string
  year: number
}

// Season helper functions - message descriptors for i18n
export const SEASON_NAMES: Record<string, MacroMessageDescriptor> = {
  spring: msg`Spring tasks`,
  summer: msg`Summer tasks`,
  fall: msg`Fall tasks`,
  winter: msg`Winter tasks`,
}

export const MONTH_RANGES: Record<string, MacroMessageDescriptor> = {
  spring: msg`March-May`,
  summer: msg`June-August`,
  fall: msg`September-November`,
  winter: msg`December-February`,
}

export const getSeasonName = (season: string): MacroMessageDescriptor => {
  return SEASON_NAMES[season] || SEASON_NAMES.spring
}

export const getMonthRange = (season: string): MacroMessageDescriptor => {
  return MONTH_RANGES[season] || MONTH_RANGES.spring
}

export const getYearDisplay = (season: string, year: number): string => {
  if (season === 'winter') {
    return `${year}-${year + 1}`
  }
  return `${year}`
}

export const getSeasonImage = (season: string): string => {
  const images: Record<string, string> = {
    spring: springImage,
    summer: summerImage,
    fall: fallImage,
    winter: winterImage,
  }
  return images[season] || springImage
}

export const getSeasonColor = (season: string): string => {
  const colors: Record<string, string> = {
    spring: '#E8F5E9',
    summer: '#FFF9C4',
    fall: '#FFE0B2',
    winter: '#E3F2FD',
  }
  return colors[season] || '#F5F5F5'
}

export const mapSchedulingTypeToSeason = (
  schedulingType: string | null,
): string => {
  if (!schedulingType) return 'spring'

  const mapping: Record<string, string> = {
    seasonal_spring: 'spring',
    seasonal_summer: 'summer',
    seasonal_fall: 'fall',
    seasonal_winter: 'winter',
  }

  return mapping[schedulingType] || 'spring'
}

// Determine season from month (1-12)
const getSeasonFromMonth = (month: number): string => {
  if (month >= 3 && month <= 5) return 'spring'
  if (month >= 6 && month <= 8) return 'summer'
  if (month >= 9 && month <= 11) return 'fall'
  return 'winter' // Dec, Jan, Feb
}

// Get the year a season belongs to, accounting for winter spanning two calendar years
// Winter 2024-2025 = Dec 2024 + Jan/Feb 2025, so Jan/Feb dates belong to previous year's winter
const getYearForSeason = (season: string, date: Date): number => {
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  return season === 'winter' && month <= 2 ? year - 1 : year
}

// Get season and year for a system task based on its scheduling type and completion date
export const getSeasonAndYearFromSchedulingType = (
  completedDate: string,
  schedulingType: string | null,
): { season: string; year: number } => {
  const date = new Date(completedDate)
  const season = mapSchedulingTypeToSeason(schedulingType)
  const year = getYearForSeason(season, date)
  return { season, year }
}

export const getSeasonAndYearFromDeadline = (
  deadline: string,
): { season: string; year: number } => {
  const date = new Date(deadline)
  const month = date.getMonth() + 1
  const season = getSeasonFromMonth(month)
  const year = getYearForSeason(season, date)
  return { season, year }
}

export const getSeasonEndDate = (season: string, year: number): string => {
  const endMonths: Record<string, number> = {
    spring: 5, // May
    summer: 8, // August
    fall: 11, // November
    winter: 2, // February (next year)
  }

  const month = endMonths[season]
  if (!month) return dayjs().format('YYYY-MM-DD')

  const targetYear = season === 'winter' ? year + 1 : year
  return dayjs()
    .year(targetYear)
    .month(month - 1)
    .endOf('month')
    .format('YYYY-MM-DD')
}

export const getCurrentSeasonIndex = (): number => {
  const month = new Date().getMonth()
  if (month >= 2 && month <= 4) return 0 // spring
  if (month >= 5 && month <= 7) return 1 // summer
  if (month >= 8 && month <= 10) return 2 // fall
  return 3 // winter
}

export const getCurrentSeason = (): string => {
  const month = new Date().getMonth()
  if (month >= 2 && month <= 4) return 'spring'
  if (month >= 5 && month <= 7) return 'summer'
  if (month >= 8 && month <= 10) return 'fall'
  return 'winter'
}

export const getTaskSeason = (task: {
  propertyId: number | null
  deadline: string | null
  schedulingType: string | null
}): string => {
  if (task.propertyId && task.deadline) {
    return getSeasonAndYearFromDeadline(task.deadline).season
  }
  return mapSchedulingTypeToSeason(task.schedulingType)
}

// Transform backend task to UI format
export const mapTaskToUI = (
  task: MaintenanceTaskWithSteps,
  completedTaskIds: Set<number>,
  isUserManaged: boolean,
): MaintenanceTask => ({
  id: task.id.toString(),
  title: task.name,
  completed: completedTaskIds.has(task.id),
  isUserTask: isUserManaged,
  isSystemTask: task.propertyId === null,
  createdByUserId: task.createdByUserId || null,
  category: task.category || undefined,
  description: task.description || undefined,
  deadline: task.deadline || undefined,
  attachments: task.attachments || undefined,
  estimatedDurationHours: task.estimatedDurationMinutes
    ? Math.round(task.estimatedDurationMinutes / 60)
    : undefined,
})

// Order tasks: incomplete first (sorted), then complete (sorted)
export const orderTasksByCompletion = <T extends { id: number }>(
  tasks: T[],
  completedIds: Set<number>,
  sortFn: (t: T) => string | number | Date,
): T[] => {
  const [incomplete, complete] = partition(
    tasks,
    (t) => !completedIds.has(t.id),
  )
  return [...sortBy(incomplete, sortFn), ...sortBy(complete, sortFn)]
}
